import React, { useState, useEffect } from 'react';
import './PayoutRequest.css';
import { useNightMode } from '../../contexts/NightModeContext';
import { useAuth } from '../../context/AuthContext';
import {
    getBankDetailsByOrganizer,
    createBankDetails,
    updateBankDetails,
    deleteBankDetails,
    getEventDetailedRevenueReport,
    createPayoutRequest,
    getPayoutsByOrganizer,
    cancelPayoutRequest,
} from '../../api';

interface BankDetails {
    bankName: string;
    accountNumber: string;
    holderName: string;
    routingNumber: string;
    accountType: string;
    verificationStatus?: string;
}

interface PayoutEvent {
    id: string;
    eventName: string;
    totalAmount: number;
    remainingAmt: number;
    status: 'Pending' | 'Requested' | 'Approved' | 'Done';
    eventStatus?: string;
}

const PayoutRequest = () => {
    const { isNightMode } = useNightMode();
    const { user } = useAuth();
    const organizerData = localStorage.getItem('organizerData');
    const organizerId = organizerData ? JSON.parse(organizerData).organizerId : null;
    
    // Core states
    const [bank, setBank] = useState<BankDetails | null>(null);
    const [bankDbId, setBankDbId] = useState<number | null>(null);
    const [events, setEvents] = useState<PayoutEvent[]>([]);
    const [payoutHistory, setPayoutHistory] = useState<any[]>([]);
    const [selectedHistoryEventId, setSelectedHistoryEventId] = useState<string>('all');

    const currency = bank?.accountType || 'NZD';

    // UI & Modal States
    const [showBankModal, setShowBankModal] = useState(false);
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<PayoutEvent | null>(null);
    const [payoutAmount, setPayoutAmount] = useState('');
    const [toast, setToast] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Form inputs for Bank Account
    const [bankForm, setBankForm] = useState({
        bankName: '',
        accountNumber: '',
        holderName: '',
        routingNumber: '',
        accountType: 'Checking'
    });

    const triggerToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(null), 3000);
    };

    const fetchBankAndPayouts = async () => {
        if (!organizerId) return;
        try {
            const bankResponse = await getBankDetailsByOrganizer(organizerId);
            const banksList = Array.isArray(bankResponse) ? bankResponse : (bankResponse ? [bankResponse] : []);
            const defaultBank = banksList.find((b: any) => b.isDefault) || banksList[0];
            if (defaultBank) {
                setBank({
                    bankName: defaultBank.bankName,
                    accountNumber: defaultBank.bankDetails?.accountNumber || '',
                    holderName: defaultBank.accountHolderName,
                    routingNumber: defaultBank.bankDetails?.swiftCode || '',
                    accountType: defaultBank.currency || 'NZD',
                    verificationStatus: defaultBank.verificationStatus
                });
                setBankDbId(defaultBank.bankDetailsId || defaultBank.id);
            } else {
                setBank(null);
                setBankDbId(null);
            }

            const [reportResponse, payoutsResponse] = await Promise.all([
                getEventDetailedRevenueReport({ organizerId }),
                getPayoutsByOrganizer(organizerId),
            ]);
            const reports = reportResponse?.data || [];
            const payoutsList = Array.isArray(payoutsResponse) ? payoutsResponse : [];
            setPayoutHistory(payoutsList);

            const mappedEvents = reports.map((rpt: any) => {
                const eventId = String(rpt.eventId);
                const sumOrganizerRevenue = rpt.sumOrganizerRevenue || 0;

                const eventPayouts = payoutsList.filter((p: any) => String(p.eventId) === eventId);
                const blockedStatuses = ['PENDING', 'APPROVED', 'PROCESSING', 'PAID'];
                const deductedAmount = eventPayouts
                    .filter((p: any) => blockedStatuses.includes(String(p.status).toUpperCase()))
                    .reduce((sum: number, p: any) => sum + parseFloat(p.requestedAmount || '0'), 0);

                const remainingAmt = Math.max(0, sumOrganizerRevenue - deductedAmount);

                const hasPendingOrProcessing = eventPayouts.some((p: any) =>
                    ['PENDING', 'PROCESSING'].includes(String(p.status).toUpperCase())
                );
                const hasApproved = eventPayouts.some((p: any) =>
                    String(p.status).toUpperCase() === 'APPROVED'
                );

                let status: 'Pending' | 'Requested' | 'Approved' | 'Done' = 'Pending';
                if (remainingAmt === 0 && !hasPendingOrProcessing && !hasApproved) {
                    status = 'Done';
                } else if (hasPendingOrProcessing) {
                    status = 'Requested';
                } else if (hasApproved) {
                    status = 'Approved';
                }

                return {
                    id: eventId,
                    eventName: rpt.title || 'Unknown Event',
                    totalAmount: sumOrganizerRevenue,
                    remainingAmt,
                    status,
                    eventStatus: rpt.eventStatus
                } as PayoutEvent;
            });

            setEvents(mappedEvents);
        } catch (err: any) {
            console.error('Error fetching bank/payout data:', err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Network error.';
            triggerToast(`Failed to load payout data: ${errMsg}`);
        }
    };

    useEffect(() => {
        if (organizerId) {
            fetchBankAndPayouts();
        }
    }, [organizerId]);

    // Bank Actions
    const handleAddBankClick = () => {
        setBankForm({
            bankName: '',
            accountNumber: '',
            holderName: '',
            routingNumber: '',
            accountType: 'Checking'
        });
        setShowBankModal(true);
    };

    const handleEditBankClick = () => {
        if (bank) {
            setBankForm({
                bankName: bank.bankName,
                accountNumber: bank.accountNumber,
                holderName: bank.holderName,
                routingNumber: bank.routingNumber,
                accountType: bank.accountType
            });
            setShowBankModal(true);
        }
    };

    const handleSaveBank = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!bankForm.bankName || !bankForm.accountNumber || !bankForm.holderName || !bankForm.routingNumber) {
            triggerToast('Please fill in all bank details.');
            return;
        }

        try {
            if (bankDbId) {
                const updatePayload = {
                    accountHolderName: bankForm.holderName,
                    bankName: bankForm.bankName,
                    bankDetails: {
                        accountNumber: bankForm.accountNumber,
                        swiftCode: bankForm.routingNumber
                    },
                    isDefault: true,
                    verificationStatus: 'VERIFIED',
                    isActive: true
                };
                await updateBankDetails(bankDbId, updatePayload);
                triggerToast('Bank account details updated successfully!');
            } else {
                const createPayload = {
                    organizerId: Number(organizerId),
                    accountHolderName: bankForm.holderName,
                    bankName: bankForm.bankName,
                    countryCode: user?.countryId || 'NZ',
                    bankDetails: {
                        accountNumber: bankForm.accountNumber,
                        swiftCode: bankForm.routingNumber
                    },
                    currency: 'NZD',
                    isDefault: true
                };
                await createBankDetails(createPayload);
                triggerToast('Bank account details saved successfully!');
            }
            setShowBankModal(false);
            fetchBankAndPayouts();
        } catch (err) {
            console.error('Error saving bank details:', err);
            triggerToast('Failed to save bank details.');
        }
    };

    const handleDisconnectBank = async () => {
        if (!bankDbId) return;
        if (window.confirm('Are you sure you want to disconnect this bank account?')) {
            try {
                await deleteBankDetails(bankDbId);
                setBank(null);
                setBankDbId(null);
                triggerToast('Bank account disconnected successfully!');
                fetchBankAndPayouts();
            } catch (err) {
                console.error('Error disconnecting bank details:', err);
                triggerToast('Failed to disconnect bank account.');
            }
        }
    };

    // Payout Actions
    const handleRequestPayoutClick = (event: PayoutEvent) => {
        if (!bank) {
            triggerToast('Please link a bank account before requesting a payout.');
            return;
        }
        if (event.eventStatus !== 'closed') {
            triggerToast('Payouts can only be requested after the event has been closed.');
            return;
        }
        setSelectedEvent(event);
        setPayoutAmount(event.remainingAmt.toString());
        setShowPayoutModal(true);
    };

    const handleConfirmPayout = async (e: React.FormEvent) => {
        e.preventDefault();
        const orgId = organizerId;
        const bankId = bankDbId;
        console.log('Confirming payout with:', { selectedEvent, orgId, bankId, payoutAmount });

        if (!selectedEvent) {
            triggerToast('Error: No event selected.');
            return;
        }
        if (!orgId) {
            triggerToast('Error: Organizer ID is missing.');
            return;
        }
        if (!bankId) {
            triggerToast('Error: Bank details ID is missing.');
            return;
        }

        const amount = parseFloat(payoutAmount);
        if (isNaN(amount) || amount <= 0) {
            triggerToast('Please enter a valid payout amount.');
            return;
        }

        if (amount > selectedEvent.remainingAmt) {
            triggerToast('Amount cannot exceed remaining balance.');
            return;
        }

        setSubmitting(true);
        try {
            const payoutPayload = {
                organizerId: Number(orgId),
                eventId: Number(selectedEvent.id),
                bankDetailsId: bankId,
                requestedAmount: amount,
                currency: currency,
                requestNotes: 'Payout for completed event'
            };
            await createPayoutRequest(payoutPayload);
            setShowPayoutModal(false);
            triggerToast(`Payout request of $${amount.toLocaleString()} ${currency} submitted successfully!`);
            fetchBankAndPayouts();
        } catch (err: any) {
            console.error('Error submitting payout request:', err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to submit payout request.';
            triggerToast(`Error: ${errMsg}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelPayout = async (payoutId: number) => {
        if (!organizerId) return;
        if (window.confirm('Are you sure you want to cancel this payout request?')) {
            try {
                await cancelPayoutRequest(payoutId, Number(organizerId));
                triggerToast('Payout request cancelled successfully!');
                fetchBankAndPayouts();
            } catch (err: any) {
                console.error('Error cancelling payout request:', err);
                const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to cancel payout request.';
                triggerToast(`Error: ${errMsg}`);
            }
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-NZ', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const getStatusBadge = (status: string) => {
        const s = status ? status.toUpperCase() : '';
        if (s === 'PAID') {
            return (
                <span className="custom-badge badge-paid">
                    <span className="badge-dot"></span>
                    Paid
                </span>
            );
        } else if (s === 'APPROVED') {
            return (
                <span className="custom-badge badge-approved">
                    <span className="badge-dot"></span>
                    Approved
                </span>
            );
        } else if (s === 'PROCESSING') {
            return (
                <span className="custom-badge badge-processing">
                    <span className="badge-dot"></span>
                    Processing
                </span>
            );
        } else if (s === 'REJECTED') {
            return (
                <span className="custom-badge badge-rejected">
                    <span className="badge-dot"></span>
                    Rejected
                </span>
            );
        } else if (s === 'CANCELLED') {
            return (
                <span className="custom-badge badge-cancelled">
                    <span className="badge-dot"></span>
                    Cancelled
                </span>
            );
        } else {
            if (bank && String(bank.verificationStatus).toUpperCase() !== 'VERIFIED') {
            return (
                <span className="custom-badge badge-pending">
                    <span className="badge-dot"></span>
                        Bank verification on process
                    </span>
                );
            }
            return (
                <span className="custom-badge badge-processing">
                    <span className="badge-dot"></span>
                    Processing
                </span>
            );
        }
    };

    const historyEvents = Array.from(
        new Map(
            payoutHistory
                .filter((p: any) => p.event)
                .map((p: any) => [String(p.eventId), p.event.title])
        ).entries()
    );

    const filteredHistory = selectedHistoryEventId === 'all'
        ? payoutHistory
        : payoutHistory.filter((p: any) => String(p.eventId) === selectedHistoryEventId);

    return (
        <div className="dashboard-body">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="d-main-title d-flex align-items-center justify-content-between">
                            <h3 style={{ fontSize: '16px' }}>
                                <i className="fa-solid fa-credit-card me-3"></i>Payout Request
                            </h3>
                            {!bank && (
                                <button
                                    className="main-btn btn-hover h_30 pe-4 ps-4 d-flex align-items-center"
                                    onClick={handleAddBankClick}
                                    style={{ gap: '6px', fontSize: '11px' }}
                                >
                                    <i className="fa-solid fa-plus"></i> Add new Bank
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bank Account Section */}
                <div className="row mt-4">
                    <div className="col-md-12">
                        {bank ? (
                            <div className={`main-card p-3 ${isNightMode ? 'bg-dark text-white' : 'bg-white text-dark'}`}>
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                    <h5 style={{ margin: 0, fontWeight: 600, fontSize: '14px' }}>Linked Bank Details</h5>
                                    <div className="d-flex gap-2">
                                        <button
                                            className="main-btn btn-hover d-flex align-items-center"
                                            onClick={handleEditBankClick}
                                            style={{ fontSize: '11px', gap: '5px', padding: '0 12px', height: '30px', borderRadius: '4px', border: 'none' }}
                                        >
                                            <i className="fa-solid fa-pen-to-square"></i> Edit Details
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger d-flex align-items-center"
                                            onClick={handleDisconnectBank}
                                            style={{ fontSize: '11px', gap: '5px', padding: '0 12px', height: '30px', borderRadius: '4px' }}
                                        >
                                            <i className="fa-solid fa-trash-can"></i> Disconnect Bank
                                        </button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-4 mb-2">
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Bank Name</div>
                                        <div style={{ fontSize: '12px', fontWeight: 600 }}>{bank.bankName}</div>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Account Holder</div>
                                        <div style={{ fontSize: '12px', fontWeight: 600 }}>{bank.holderName}</div>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Account Number</div>
                                        <div style={{ fontSize: '12px', fontWeight: 600, fontFamily: 'monospace' }}>
                                            {bank.accountNumber ? `•••• •••• •••• ${bank.accountNumber.slice(-4)}` : ''}
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Routing Number / IFSC</div>
                                        <div style={{ fontSize: '12px', fontWeight: 600 }}>{bank.routingNumber}</div>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Account Type</div>
                                        <div style={{ fontSize: '12px', fontWeight: 600 }}>{bank.accountType}</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div 
                                className={`main-card text-center p-5 ${isNightMode ? 'bg-dark text-light' : 'bg-light text-dark'}`} 
                                style={{ border: '2px dashed rgba(0,0,0,0.15)', borderRadius: '12px' }}
                            >
                                <i className="fa-solid fa-building-columns fa-3x mb-3 text-muted"></i>
                                <h4>No Bank Account Added</h4>
                                <p className="text-muted mb-4">Please add your bank account details to enable payout requests for your ticket revenue.</p>
                                <button
                                    className="main-btn btn-hover h_40 pe-4 ps-4"
                                    onClick={handleAddBankClick}
                                >
                                    <i className="fa-solid fa-plus me-2"></i> Add Bank Account
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Payout History Table */}
                <div className="row mt-3">
                    <div className="col-md-12">
                        <div className={`main-card ${isNightMode ? 'bg-dark text-white' : 'bg-white text-dark'}`}>
                            <div className="dashboard-wrap-content p-3">
                                <h5 className="mb-3" style={{ fontWeight: 600, fontSize: '14px' }}>Event Revenue & Payouts</h5>
                                <div className="table-responsive">
                                    <table className={`table ${isNightMode ? 'table-dark' : 'table-striped'} align-middle`} style={{ margin: 0 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ padding: '8px 12px' }}>Event Name</th>
                                                <th style={{ padding: '8px 12px' }}>Total Amount</th>
                                                <th style={{ padding: '8px 12px' }}>Remaining Amt</th>
                                                <th style={{ padding: '8px 12px' }}>Status</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {events.map((evt) => (
                                                <tr key={evt.id}>
                                                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{evt.eventName}</td>
                                                    <td style={{ padding: '8px 12px' }}>${evt.totalAmount.toLocaleString()} {currency}</td>
                                                    <td style={{ padding: '8px 12px' }}>${evt.remainingAmt.toLocaleString()} {currency}</td>
                                                    <td style={{ padding: '8px 12px' }}>
                                                        {(() => {
                                                            if (evt.status === 'Done') {
                                                                return (
                                                                    <span className="custom-badge badge-paid">
                                                            <span className="badge-dot"></span>
                                                                        Settled
                                                        </span>
                                                                );
                                                            }
                                                            if (evt.status === 'Approved') {
                                                                return (
                                                                    <span className="custom-badge badge-approved">
                                                                        <span className="badge-dot"></span>
                                                                        Approved
                                                                    </span>
                                                                );
                                                            }
                                                            if (evt.status === 'Requested') {
                                                                if (bank && String(bank.verificationStatus).toUpperCase() !== 'VERIFIED') {
                                                                    return (
                                                                        <span className="custom-badge badge-pending">
                                                                            <span className="badge-dot"></span>
                                                                            Bank verification on process
                                                                        </span>
                                                                    );
                                                                }
                                                                return (
                                                                    <span className="custom-badge badge-processing">
                                                                        <span className="badge-dot"></span>
                                                                        Processing
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <span className="custom-badge badge-pending">
                                                                    <span className="badge-dot"></span>
                                                                    Pending
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                                        {evt.remainingAmt > 0 ? (
                                                            evt.eventStatus === 'closed' ? (
                                                                <button
                                                                    className="main-btn btn-hover h_30 pe-3 ps-3"
                                                                    onClick={() => handleRequestPayoutClick(evt)}
                                                                    disabled={!bank}
                                                                    style={{ 
                                                                        fontSize: '11px', 
                                                                        opacity: !bank ? 0.5 : 1, 
                                                                        backgroundColor: !bank ? '#94a3b8' : undefined,
                                                                        borderColor: !bank ? '#94a3b8' : undefined,
                                                                        color: !bank ? '#ffffff' : undefined
                                                                    }}
                                                                >
                                                                    Request Payout
                                                                </button>
                                                            ) : (
                                                                <div className="d-flex align-items-center justify-content-end gap-2">
                                                                    <button
                                                                        className="main-btn h_30 pe-3 ps-3"
                                                                        disabled
                                                                        style={{ 
                                                                            fontSize: '11px', 
                                                                            opacity: 0.5, 
                                                                            backgroundColor: '#94a3b8',
                                                                            borderColor: '#94a3b8',
                                                                            color: '#ffffff',
                                                                            cursor: 'not-allowed'
                                                                        }}
                                                                    >
                                                                        Request Payout
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => triggerToast('Payouts can only be requested after the event has been closed.')}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            color: '#ef4444',
                                                                            cursor: 'pointer',
                                                                            fontSize: '14px',
                                                                            padding: '4px',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                        title="Payouts can only be requested after the event has been closed."
                                                                    >
                                                                        <i className="fa-solid fa-circle-exclamation"></i>
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : evt.status === 'Requested' ? (
                                                            <button
                                                                className="main-btn btn-hover h_30 pe-3 ps-3"
                                                                disabled
                                                                style={{ 
                                                                    fontSize: '11px', 
                                                                    opacity: 0.5, 
                                                                    backgroundColor: '#94a3b8',
                                                                    borderColor: '#94a3b8',
                                                                    color: '#ffffff'
                                                                }}
                                                            >
                                                                Pending Approval
                                                            </button>
                                                        ) : evt.status === 'Approved' ? (
                                                            <button
                                                                className="main-btn btn-hover h_30 pe-3 ps-3"
                                                                disabled
                                                                style={{ 
                                                                    fontSize: '11px', 
                                                                    opacity: 0.5, 
                                                                    backgroundColor: '#94a3b8',
                                                                    borderColor: '#94a3b8',
                                                                    color: '#ffffff'
                                                                }}
                                                            >
                                                                Approved
                                                            </button>
                                                        ) : (
                                                            <span className="text-success" style={{ fontSize: '11px', fontWeight: 600 }}>
                                                                <i className="fa-solid fa-circle-check me-1"></i> Settled
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payout History Section */}
                <div className="row mt-4">
                    <div className="col-md-12">
                        <div className={`main-card ${isNightMode ? 'bg-dark text-white' : 'bg-white text-dark'}`}>
                            <div className="dashboard-wrap-content p-3">
                                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                                    <h5 className="mb-0" style={{ fontWeight: 600, fontSize: '14px' }}>Payout History</h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Filter by Event:</span>
                                        <select
                                            className="form-select form-select-sm"
                                            value={selectedHistoryEventId}
                                            onChange={(e) => setSelectedHistoryEventId(e.target.value)}
                                            style={{ 
                                                width: '240px', 
                                                fontSize: '11px', 
                                                height: '30px', 
                                                borderRadius: '4px',
                                                backgroundColor: isNightMode ? '#1e293b' : '#ffffff',
                                                color: isNightMode ? '#ffffff' : '#333333',
                                                borderColor: isNightMode ? '#475569' : '#cbd5e1',
                                                padding: '0 10px'
                                            }}
                                        >
                                            <option value="all">All Events</option>
                                            {historyEvents.map(([id, title]) => (
                                                <option key={id} value={id}>
                                                    {title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className={`table ${isNightMode ? 'table-dark' : 'table-striped'} align-middle`} style={{ margin: 0 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ padding: '8px 12px' }}>Request ID</th>
                                                <th style={{ padding: '8px 12px' }}>Requested Date</th>
                                                <th style={{ padding: '8px 12px' }}>Event Name</th>
                                                <th style={{ padding: '8px 12px' }}>Amount</th>
                                                <th style={{ padding: '8px 12px' }}>Status</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredHistory.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted" style={{ padding: '20px' }}>
                                                        No payout history found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredHistory.map((payout) => (
                                                    <tr key={payout.payoutId}>
                                                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>#{payout.payoutId}</td>
                                                        <td style={{ padding: '8px 12px' }}>{formatDate(payout.requestedDate)}</td>
                                                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{payout.event?.title || 'Unknown Event'}</td>
                                                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                                                            ${parseFloat(payout.requestedAmount || '0').toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {payout.currency || 'NZD'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px' }}>
                                                            {getStatusBadge(payout.status)}
                                                            {payout.status === 'REJECTED' && payout.rejectionReason && (
                                                                <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px', fontWeight: 500 }}>
                                                                    Reason: {payout.rejectionReason}
                                                                </div>
                                                            )}
                                                            {payout.status?.toUpperCase() === 'PAID' && (
                                                                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', lineHeight: '1.4' }}>
                                                                    {payout.referenceNumber && (
                                                                        <div>Ref: {payout.referenceNumber}</div>
                                                                    )}
                                                                    {payout.payoutSentDate && (
                                                                        <div>Paid Date: {formatDate(payout.payoutSentDate)}</div>
                                                                    )}
                                                                    {payout.payoutSentAmount && (
                                                                        <div>Paid Amt: ${parseFloat(payout.payoutSentAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })} {payout.currency || 'NZD'}</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {payout.adminNotes && (
                                                                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                                                                    Note: {payout.adminNotes}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                                            {payout.status === 'PENDING' && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleCancelPayout(payout.payoutId)}
                                                                    style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bank Modal */}
            {showBankModal && (
                <div className="custom-modal-overlay">
                    <div 
                        className="custom-modal-content"
                        style={{ backgroundColor: isNightMode ? '#1a1a26' : '#ffffff', color: isNightMode ? '#ffffff' : '#333333' }}
                    >
                        <h4 className="mb-4" style={{ fontWeight: 600 }}>
                            {bank ? 'Edit Bank Account' : 'Add Bank Account'}
                        </h4>
                        <form onSubmit={handleSaveBank}>
                            <div className="form-group mb-3">
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: 500 }}>Bank Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankForm.bankName}
                                    onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                                    placeholder="e.g. Chase Bank"
                                    required
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: 500 }}>Account Holder Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankForm.holderName}
                                    onChange={(e) => setBankForm({ ...bankForm, holderName: e.target.value })}
                                    placeholder="Name on account"
                                    required
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: 500 }}>Account Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankForm.accountNumber}
                                    onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value.replace(/[^0-9]/g, '') })}
                                    placeholder="Enter digits only"
                                    required
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: 500 }}>Routing Number / IFSC</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bankForm.routingNumber}
                                    onChange={(e) => setBankForm({ ...bankForm, routingNumber: e.target.value })}
                                    placeholder="Enter routing number"
                                    required
                                />
                            </div>
                            <div className="form-group mb-4">
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: 500 }}>Account Type</label>
                                <select
                                    className="form-control"
                                    value={bankForm.accountType}
                                    onChange={(e) => setBankForm({ ...bankForm, accountType: e.target.value })}
                                >
                                    <option value="Checking">Checking</option>
                                    <option value="Savings">Savings</option>
                                </select>
                            </div>
                            <div className="d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowBankModal(false)}
                                    style={{ borderRadius: '6px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ borderRadius: '6px' }}
                                >
                                    Save Details
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Payout Request Modal */}
            {showPayoutModal && selectedEvent && (
                <div className="custom-modal-overlay">
                    <div 
                        className="custom-modal-content"
                        style={{ backgroundColor: isNightMode ? '#1a1a26' : '#ffffff', color: isNightMode ? '#ffffff' : '#333333' }}
                    >
                        <h4 className="mb-3" style={{ fontWeight: 600 }}>Confirm Payout Request</h4>
                        <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                            You are requesting a ticket revenue payout for the event: <strong style={{ color: isNightMode ? '#fff' : '#000' }}>{selectedEvent.eventName}</strong>.
                        </p>
                        
                        <div 
                            className="p-3 mb-4 rounded" 
                            style={{ backgroundColor: isNightMode ? '#222233' : '#f8fafc', border: '1px solid rgba(0,0,0,0.05)' }}
                        >
                            <div className="d-flex justify-content-between mb-2">
                                <span style={{ fontSize: '13px', color: '#64748b' }}>Total Revenue</span>
                                <span style={{ fontWeight: 600 }}>${selectedEvent.totalAmount.toLocaleString()} {currency}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span style={{ fontSize: '13px', color: '#64748b' }}>Remaining Balance</span>
                                <span style={{ fontWeight: 600 }}>${selectedEvent.remainingAmt.toLocaleString()} {currency}</span>
                            </div>
                            {bank && (
                                <div className="d-flex justify-content-between">
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Destination</span>
                                    <span style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {bank.bankName} ({bank.accountNumber.slice(-4)})
                                    </span>
                                </div>
                            )}
                        </div>

                        {bank && String(bank.verificationStatus).toUpperCase() !== 'VERIFIED' && (
                            <div 
                                className="p-3 mb-4 rounded d-flex align-items-center gap-2" 
                                style={{ 
                                    backgroundColor: 'rgba(245, 158, 11, 0.15)', 
                                    border: '1px solid rgba(245, 158, 11, 0.25)',
                                    color: '#d97706',
                                    fontSize: '13px',
                                    fontWeight: 500
                                }}
                            >
                                <i className="fa-solid fa-circle-exclamation" style={{ color: '#d97706', fontSize: '16px' }}></i>
                                <span>Note: Your bank account verification is in progress. The payout will be processed once the admin verifies your bank.</span>
                            </div>
                        )}

                        <form onSubmit={handleConfirmPayout}>
                            <div className="form-group mb-4">
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: 500 }}>Payout Amount ({currency})</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={payoutAmount}
                                    onChange={(e) => setPayoutAmount(e.target.value)}
                                    max={selectedEvent.remainingAmt}
                                    min="1"
                                    required
                                />
                                <small className="text-muted mt-1 d-block">
                                    Enter an amount up to ${selectedEvent.remainingAmt.toLocaleString()} {currency}
                                </small>
                            </div>
                            <div className="d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowPayoutModal(false)}
                                    style={{ borderRadius: '6px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ borderRadius: '6px' }}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Submitting...' : 'Confirm Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom Toast Notification */}
            {toast && (
                <div 
                    style={{
                        position: 'fixed',
                        bottom: '24px',
                        right: '24px',
                        backgroundColor: '#1e293b',
                        color: '#ffffff',
                        padding: '14px 24px',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
                        zIndex: 1100,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontWeight: 500,
                        fontSize: '14px',
                        animation: 'toastSlide 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                    }}
                >
                    <i className="fa-solid fa-circle-check" style={{ color: '#10b981', fontSize: '18px' }}></i>
                    <span>{toast}</span>
                </div>
            )}
        </div>
    );
};

export default PayoutRequest;
